import { Habit, Member, RedemptionItem, RedemptionRecord } from '../types';
import { getSupabase } from './supabaseClient';
import { COLORS } from '../constants';
import {
  getAllHabitsFromDB,
  saveHabitsToDB,
  getAllMembersFromDB,
  saveMembersToDB,
  getAllRedemptionItemsFromDB,
  saveRedemptionItemsToDB,
  getAllRedemptionRecordsFromDB,
  saveRedemptionRecordsToDB
} from './db';

const STORAGE_KEY = 'habitsync_data';
const MEMBERS_KEY = 'habitsync_members';
const USER_ID_KEY = 'habitsync_uid';
const DARK_MODE_KEY = 'habitsync_dark_mode';

const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const getUserId = (): string => {
  let uid = localStorage.getItem(USER_ID_KEY);
  if (!uid) {
    uid = generateUUID();
    localStorage.setItem(USER_ID_KEY, uid);
  }
  return uid;
};

// --- Settings Storage ---

export const getDarkMode = () => localStorage.getItem(DARK_MODE_KEY) === 'true';
export const saveDarkMode = (isDark: boolean) => localStorage.setItem(DARK_MODE_KEY, String(isDark));

// --- Members Storage ---

export const saveMembersLocal = async (members: Member[]) => {
  // Update timestamps
  const current = await getAllMembersFromDB();
  const map = new Map(current.map(m => [m.id, m]));

  const updatedMembers = members.map(m => {
    const old = map.get(m.id);
    // If new or changed (naive JSON comparison), update timestamp if not already newer
    if (!old || JSON.stringify(old) !== JSON.stringify(m)) {
      // If caller didn't update timestamp, we do it
      // But how do we know if caller updated it?
      // We compare m.updatedAt to old.updatedAt?
      // If m.updatedAt is same as old.updatedAt, then caller didn't update.
      if (!old || m.updatedAt === old.updatedAt) {
        return { ...m, updatedAt: new Date().toISOString() };
      }
    }
    return m;
  });

  await saveMembersToDB(updatedMembers);
};

export const getMembersLocal = async (): Promise<Member[]> => {
  // Try DB first
  let members = await getAllMembersFromDB();

  if (members.length === 0) {
    // Migraiton: Try LocalStorage
    const data = localStorage.getItem(MEMBERS_KEY);
    if (data) {
      members = JSON.parse(data);
      // Save to DB
      await saveMembersToDB(members);
    } else {
      // Default member if none exists
      const defaultMember: Member = {
        id: 'default-user',
        name: '我',
        avatarColor: COLORS[0],
        createdAt: new Date().toISOString()
      };
      members = [defaultMember];
      await saveMembersToDB(members);
    }
  }
  return members;
};

// --- Habits Storage ---

export const saveHabitsLocal = async (habits: Habit[]) => {
  const current = await getAllHabitsFromDB();
  const map = new Map(current.map(h => [h.id, h]));

  const updatedHabits = habits.map(h => {
    const old = map.get(h.id);
    if (!old || JSON.stringify(old) !== JSON.stringify(h)) {
      if (!old || h.updatedAt === old.updatedAt) {
        return { ...h, updatedAt: new Date().toISOString() };
      }
    }
    return h;
  });

  await saveHabitsToDB(updatedHabits);
};

export const getHabitsLocal = async (): Promise<Habit[]> => {
  // Try DB first
  let habits = await getAllHabitsFromDB();
  let loadedFromLocalStorage = false;

  // Migration: Try LocalStorage if DB is empty
  if (habits.length === 0) {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      habits = JSON.parse(data);
      loadedFromLocalStorage = true;
    }
  }

  // Migration Logic (Structure updates)
  const members = await getMembersLocal();
  const defaultMemberId = members[0].id;

  let needsSave = false;
  habits = habits.map(h => {
    let updated = false;

    // 1. Migrate Member ID
    if (!h.memberId) {
      h.memberId = defaultMemberId;
      updated = true;
    }

    // 2. Migrate Milestones (Old targetStreak/rewardText to milestones array)
    if (!h.milestones) {
      if (h.targetStreak && h.targetStreak > 0) {
        h.milestones = [{
          target: h.targetStreak,
          reward: h.rewardText || '完成目标'
        }];
      } else {
        h.milestones = [];
      }
      updated = true;
    }

    if (updated) needsSave = true;
    return h;
  });

  if (loadedFromLocalStorage || needsSave) {
    await saveHabitsLocal(habits);
  }

  return habits;
};

export const saveHabits = async (habits: Habit[]) => {
  // Only save local automatically. Cloud save is now explicit via UI.
  await saveHabitsLocal(habits);
};

export const loadHabits = async (): Promise<Habit[]> => {
  return await getHabitsLocal();
};

// --- Redemption Storage ---

export const getRedemptionItemsLocal = async (): Promise<RedemptionItem[]> => {
  return await getAllRedemptionItemsFromDB();
};

export const saveRedemptionItemsLocal = async (items: RedemptionItem[]) => {
  const current = await getAllRedemptionItemsFromDB();
  const map = new Map(current.map(i => [i.id, i]));

  const updatedItems = items.map(i => {
    const old = map.get(i.id);
    if (!old || JSON.stringify(old) !== JSON.stringify(i)) {
      if (!old || i.updatedAt === old.updatedAt) {
        return { ...i, updatedAt: new Date().toISOString() };
      }
    }
    return i;
  });

  await saveRedemptionItemsToDB(updatedItems);
};

export const getRedemptionRecordsLocal = async (): Promise<RedemptionRecord[]> => {
  return await getAllRedemptionRecordsFromDB();
};

export const saveRedemptionRecordsLocal = async (records: RedemptionRecord[]) => {
  await saveRedemptionRecordsToDB(records);
};

// --- File Backup (Local JSON) ---

export const exportDataToJson = (habits: Habit[], members: Member[], redemptionItems: RedemptionItem[] = [], redemptionRecords: RedemptionRecord[] = [], healthProfiles: any[] = [], healthRecords: any[] = []) => {
  const data = {
    version: 6,
    exportedAt: new Date().toISOString(),
    habits,
    members,
    redemptionItems,
    redemptionRecords,
    healthProfiles,
    healthRecords
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `HabitSync_Backup_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const importDataFromJson = (file: File): Promise<{ habits: Habit[], members: Member[], redemptionItems: RedemptionItem[], redemptionRecords: RedemptionRecord[], healthProfiles: any[], healthRecords: any[] }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        if (!json.habits || !Array.isArray(json.habits)) {
          reject(new Error("Invalid backup file format"));
          return;
        }

        let members = json.members;
        if (!members) {
          members = await getMembersLocal(); // fallback to current if missing
        }

        resolve({
          habits: json.habits,
          members: members,
          redemptionItems: json.redemptionItems || [],
          redemptionRecords: json.redemptionRecords || [],
          healthProfiles: json.healthProfiles || [],
          healthRecords: json.healthRecords || []
        });
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsText(file);
  });
};